<template>
  <Flex vis-container grow v-bind:class="{ 'pt-3': !embedded }" vld-parent>
    <loading :active.sync="isScreenshotting" background-color="#f0f0f0" loader="bars"> </loading>
    <SideBar v-if="!embedded" :isMainChartLoading="loading" v-bind="$attrs" />
    <Banner :alert="notification" @close="onBannerClose" />
    <Flex column grow center ref="chartWrapper">
      <Spinner v-if="loading"></Spinner>
      <ChartPlaceholder v-if="placeHolderMessage && !loading" :message="placeHolderMessage" />
      <Title
        ref="titleView"
        v-if="$route.query.category && showTitle($route.name)"
        v-show="isTitleVisible()"
        :titleText="$route.query.titleText"
        :embedded="embedded"
      />
      <router-view
        ref="chartView"
        @loaded="onLoadComplete"
        v-bind="$attrs"
        v-show="isChartVisible()"
        v-if="$route.query.category"
        :key="$route.fullPath"
      />
      <ListViewLink
        class="list-view-link"
        v-if="embedded && !chartLoading && showListViewLink($route.name)"
        v-bind="$attrs"
      />
    </Flex>
  </Flex>
</template>

<script>
import Flex from '@wizer/core/components/flex/Flex'
import Spinner from '@wizer/core/components/spinner/Spinner'
import { mapActions, mapState } from 'vuex'
import { find, isEmpty, isNil } from 'lodash'
import html2canvas from 'html2canvas'
import Loading from 'vue-loading-overlay'
import ListViewLink from '@/components/list-view-visualisation/ListViewLink'
import Title from '@/components/list-view-visualisation/chart/Title'
import SideBar from '@/components/list-view-visualisation/SideBar'
import Banner from '@/components/Banner'
import IframeMixin from '@/mixins/IframeMixin'
import ChartPlaceholder from '@/components/list-view-visualisation/ChartPlaceholder'
import ChartConfigurations from '@/components/list-view-visualisation/chart/ChartConfigurations'

export default {
  name: 'ListViewVisualisation',
  mixins: [IframeMixin],

  components: {
    ChartPlaceholder,
    Flex,
    Spinner,
    SideBar,
    Banner,
    ListViewLink,
    Title,
    Loading,
  },
  data() {
    return {
      isScreenshotting: false,
      chartLoading: true,
      chartContainsData: false,
    }
  },
  methods: {
    takeScreenshot() {
      if (this.$refs.chartView.prepareForScreenShot) {
        this.$refs.chartView.prepareForScreenShot(true)
      }
      // Postpone execution to the callback queue, as only then, the map chart control buttons
      // will be already hidden
      setTimeout(this.checkChartLoadStatus, 0)
    },
    // Simple polling mechanism which lets canvas generation to start when the chart is fully loaded
    // on the client side.
    // the chartLoading flag indicates server side call to get chart data
    // the isChartViewFullyRendered flag indicates chart rendering state on the client side
    checkChartLoadStatus() {
      if (!this.chartLoading && this.isChartViewFullyRendered) {
        this.generateCanvas()
      } else {
        setTimeout(this.checkChartLoadStatus, 100)
      }
    },
    generateCanvas() {
      this.isScreenshotting = true
      html2canvas(this.$refs.chartWrapper.$el, {
        logging: false,
        useCORS: true,
        scale: 1,
      })
        .then((canvas) => {
          const title = this.$route.query.titleText || this.storedTitleText || 'visualization'
          this.saveAs(canvas.toDataURL('image/png'), `${title}_${this.getTimestamp()}.png`)
        })
        .finally(() => {
          if (this.$refs.chartView.prepareForScreenShot) {
            this.$refs.chartView.prepareForScreenShot(false)
          }
          this.isScreenshotting = false
        })
    },
    getTimestamp() {
      const currentDate = new Date()
      const paddedMonth = `${currentDate.getMonth() + 1}`.padStart(2, '0')
      const paddedDate = `${currentDate.getDate()}`.padStart(2, '0')
      const paddedHours = `${currentDate.getHours()}`.padStart(2, '0')
      const paddedMinutes = `${currentDate.getMinutes()}`.padStart(2, '0')
      return `_${currentDate.getFullYear()}${paddedMonth}${paddedDate}_${paddedHours}${paddedMinutes}`
    },
    saveAs(uri, filename) {
      const link = document.createElement('a')

      if (typeof link.download === 'string') {
        link.href = uri
        link.download = filename

        // Firefox requires the link to be in the body
        document.body.appendChild(link)

        // simulate click
        link.click()

        // remove the link when done
        document.body.removeChild(link)
      } else {
        window.open(uri)
      }
    },
    showListViewLink(routeName) {
      return find(ChartConfigurations, (config) => config.name === routeName).showListViewLink
    },
    showTitle(routeName) {
      return find(ChartConfigurations, (config) => config.name === routeName).showTitle
    },
    onBannerClose() {
      this.removeNotification(this.notifications.slice(-1)[0])
    },
    onLoadComplete(chartContainsData) {
      this.chartLoading = false
      this.chartContainsData = chartContainsData
    },
    isChartVisible() {
      if (!this.chartLoading && this.chartContainsData) {
        this.$nextTick(this.$refs.chartView.onChartVisible)
        return true
      }
      return false
    },
    isTitleVisible() {
      if (
        !this.chartLoading &&
        this.chartContainsData &&
        (this.$route.query.titleText || this.storedTitleText)
      ) {
        this.$nextTick(this.$refs.titleView.onTitleVisible)
        return true
      }
      return false
    },
    postVueSuccessMessage() {
      const content = window.parent
      content.postMessage(
        {
          status: 'vue-success',
        },
        window.location.origin,
      )
    },
    ...mapActions({ removeNotification: 'Notification/remove' }),
  },
  computed: {
    isChartViewFullyRendered() {
      return isNil(this.$refs.chartView.isFullyRendered) || this.$refs.chartView.isFullyRendered
    },
    embedded() {
      return this.$route.query.embedded === 'true'
    },
    loading() {
      if (!this.embedded && this.categoriesLoaded && isEmpty(this.categories)) {
        return false
      }
      return this.chartLoading
    },
    notification() {
      if (!this.notifications.length) return null
      return this.notifications.slice(-1)[0]
    },
    ...mapState({
      notifications: (state) => state.Notification.notifications,
      categories: (state) => state.ChartCategory.categories,
      categoriesLoaded: (state) => state.ChartCategory.categoriesLoaded,
      placeHolderMessage: (state) => state.ChartCategory.placeHolderMessage,
      storedTitleText: (state) => state.ChartCategory.titleText,
    }),
  },
  mounted() {
    this.registerProcessCallback(this.postVueSuccessMessage)
    this.registerListener('vue-lvv-genScreenshot', this.takeScreenshot)
  },
  beforeRouteUpdate(to, from, next) {
    next()
    this.chartLoading = true
    if (!isNil(this.$refs.chartView) && !isNil(this.$refs.chartView.isFullyRendered)) {
      this.$refs.chartView.isFullyRendered = false
    }
  },
}
</script>
<style lang="postcss" scoped>
.list-view-link {
  @apply self-end mr-2 invisible h-5;
  padding-top: 1px;
  padding-bottom: 1px;
}
.vis-container:hover .list-view-link {
  @apply visible;
}
.vis-container {
  @apply relative;
  overflow: hidden !important;
}
</style>
